import { Group, Modal, Stack, UnstyledButton, useMantineTheme } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useTranslation from 'next-translate/useTranslation';
import { useContext, useEffect, useRef, useState } from 'react';

import { UserContext } from '~/components/providers/UserProvider';
import { reportEvent } from '~/domains/analytics';
import { SplashLoader } from '~/domains/common/components/SplashLoader/SplashLoader';
import { Button } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import { PaymentsQueryKeys } from '~/domains/payments/query';
import { CryptoCooldownError } from '~/services/wallet/interfaces/crypto-direct.interface';
import type { ICryptoDepositAddresses } from '~/services/wallet/interfaces/crypto-direct.interface';
import { createCryptoDepositAddresses } from '~/services/wallet/wallet.service.api';
import dt from '~/testing';

import { CryptoAddressPanel } from './CryptoAddressPanel';
import { formatAddressChangedDate, shouldShowAddressChangedNotice } from './addressChangedNotice';
import {
  CRYPTO_SETUP_DEADLINE_MS,
  CRYPTO_SETUP_MAX_RETRIES,
  CRYPTO_SETUP_RETRY_MS,
} from './constants';

type CryptoAddressSheetProps = {
  opened: boolean;
  onClose: VoidFunction;
  /** Route back to Screen 1 (the network list), clearing BOTH the network and asset picks. Called
   *  from the terminal `networkUnavailable` phase's back affordance — a permanently-absent picked
   *  network is a dead end for THIS network, so the only forward path is choosing another one. */
  onBackToNetworkList: VoidFunction;
  /** Network display name, e.g. "Ethereum", "Solana" — matched against `networks[].name` on the
   *  create response (NEVER assume index 0; the picked network can land anywhere in the array). */
  network: string;
  asset: string;
};

type Phase =
  | 'initial'
  | 'loading'
  | 'success'
  // TERMINAL: the create response succeeded but has no address for the picked network AND the
  // account is not `provisioning` — a PERMANENT condition (a same-family address divergence, or any
  // non-provisioning partial-provisioning shape). Distinct from the transient `settingUp` retry
  // cycle, which is entered by `provisioning` ONLY. Its own copy + a back-to-network-list route.
  | 'networkUnavailable'
  | 'settingUp'
  | 'settingUpTimeout'
  | 'error'
  | 'cooldown'
  | 'cooldownUnknown';

const formatCooldownTime = (retryAfterAt: string | null) =>
  retryAfterAt ? new Date(retryAfterAt).toLocaleTimeString() : '';

/**
 * Screen 3 of the gated crypto deposit flow (task B5). Confirm -> create the deposit address ->
 * reveal the PICKED network's address, with a bounded "setting up" retry cycle for a provisioning
 * account (see constants.ts) and Retry affordances for a transient failure or a cooldown.
 */
export function CryptoAddressSheet({
  opened,
  onClose,
  onBackToNetworkList,
  network,
  asset,
}: CryptoAddressSheetProps) {
  const { t } = useTranslation('payments');
  const theme = useMantineTheme();
  const queryClient = useQueryClient();
  const { user } = useContext(UserContext);

  // Ref-counted (not React state) so the check-then-arm happens synchronously from onSettled —
  // see the RED report's note on why a setState functional-updater is unreliable here.
  const retryCountRef = useRef(0);
  // EDGE-32105 same-tick double-submit guard (see startOrRetry). The `'loading'` lock is the PRIMARY
  // prevention; this ref closes the residual SAME-TICK double-tap that beats the button's unmount. It
  // tracks IN-FLIGHT state (true only while a user-initiated create is unsettled), NOT a wall-clock
  // window — so it swallows the same-tick second press exactly and CANNOT outlive the request, which is
  // what keeps a post-settle Retry from being silently swallowed (Milan #3730 review; the earlier 1s
  // timestamp window swallowed a Retry pressed within 1s of a fast failure). Cleared on every settle
  // (top of onSettled) and on the 60s deadline path — every path that renders a fresh button.
  const isSubmittingRef = useRef(false);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const hasFiredAnalyticsRef = useRef(false);
  // Focus targets for the lock-transition focus effect (WCAG 2.4.3). `dialogBodyRef` sits inside the
  // Modal's Paper (role="dialog"), so `.closest('[role="dialog"]')` reaches the exact dialog node;
  // `retryButtonRef` is the primary action to land on when the lock releases into a Retry phase.
  const dialogBodyRef = useRef<HTMLDivElement>(null);
  const retryButtonRef = useRef<HTMLButtonElement>(null);
  // The 60s overall cycle deadline (CRYPTO_SETUP_DEADLINE_MS). Armed ONCE per cycle at entry to
  // settingUp (see the arm effect below), never per attempt. `deadlineArmedRef` is the once-per-cycle
  // latch (reset only in startOrRetry / unmount, NOT in the terminal-clear effect).
  const deadlineTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const deadlineArmedRef = useRef(false);
  // Per-cycle AbortController: the create/poll POST is issued with its signal, so the deadline can
  // cancel an in-flight poll. Recreated in startOrRetry (a fresh cycle gets a fresh controller).
  const abortControllerRef = useRef<AbortController>();
  // Ref mirror of `isSettingUpTimedOut` (state is unreliable inside the mutation's fire-and-forget
  // onSettled — same reason isClosedRef is a ref): a late settle of an aborted/deadlined poll must
  // read the CURRENT timed-out state and short-circuit BEFORE re-arming a retry timer.
  const isSettingUpTimedOutRef = useRef(false);
  // Set true in the unmount cleanup so the mutation's onSuccess/onSettled — which in TanStack Query
  // v4 fire UNCONDITIONALLY from Mutation.execute() even after the observer unsubscribes — become
  // no-ops. Without this, closing the sheet while a create POST is in flight lets onSettled arm a
  // fresh retry timer on the dead instance (never cleared), firing real POSTs from a closed screen.
  const isClosedRef = useRef(false);
  const [isSettingUpTimedOut, setIsSettingUpTimedOut] = useState(false);
  // True once we've seen at least one `provisioning` response for the current cycle, so the brief
  // window between a re-fire's dispatch and its settlement still renders "setting up" instead of
  // flashing back to the initial button.
  const [isProvisioningCycle, setIsProvisioningCycle] = useState(false);

  useEffect(() => {
    // Reset on (re)mount: a React 18 StrictMode double-mount (setup → cleanup → setup, refs
    // preserved) runs the cleanup below — which sets isClosedRef.current = true — BETWEEN the two
    // setups. Without this reset the ref stays stuck true, and onSuccess/onSettled short-circuit on
    // a live sheet (the address still reveals off mutation.data, but the cache write + analytics
    // never fire).
    isClosedRef.current = false;
    return () => {
      isClosedRef.current = true;
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      // Also clear the 60s deadline (it would otherwise fire abort()+setState on a dead instance).
      // No abort() here — isClosedRef already neutralizes a late-landing POST (deliberate design).
      if (deadlineTimeoutRef.current) clearTimeout(deadlineTimeoutRef.current);
    };
  }, []);

  const mutation = useMutation({
    mutationKey: PaymentsQueryKeys.CREATE_CRYPTO_DEPOSIT_ADDRESSES,
    // Arrow wrapper (never the bare reference): forwards the current cycle's AbortSignal so the 60s
    // deadline can cancel an in-flight poll. A bare reference would receive RQ's `variables` as the
    // first arg — which would land in the new `signal` parameter.
    mutationFn: () => createCryptoDepositAddresses(abortControllerRef.current?.signal),
    onSuccess: (data) => {
      // Post-unmount / post-deadline guard (mirrors onSettled): a POST that lands after close (v4
      // fires onSuccess unconditionally from Mutation.execute()) OR after the 60s deadline already
      // fired (settingUpTimeout, lock released) must NOT write into the shared container cache. A
      // late settle of an aborted/deadlined poll would otherwise stomp the cache from a cycle the
      // user has already been shown to have timed out.
      if (isClosedRef.current || isSettingUpTimedOutRef.current) return;
      // Scoped merge: a degraded create response (`catalog: []`) must never blank the picker
      // rendered behind this sheet — only fall back to `data.catalog` when `prev` has none either.
      // `accountStatus` is DELIBERATELY NOT merged: this cache entry drives the container's top-level
      // screen gate (`isActive`) and the arrivals-poll predicate off the geo-gated GET; a POST for
      // one network/asset pick returning `provisioning`/`unknown` must never flip that gate and tear
      // the sheet out from under the user or freeze the arrivals poll.
      queryClient.setQueryData<ICryptoDepositAddresses>(
        PaymentsQueryKeys.CRYPTO_DEPOSIT_ADDRESSES(user?.id),
        (prev) => ({
          ...(prev ?? data),
          networks: data.networks,
          disclosedAt: data.disclosedAt,
          catalog: data.catalog.length ? data.catalog : (prev?.catalog ?? []),
        })
      );

      const revealed = data.networks.find((n) => n.name === network) ?? null;
      // Guard against re-firing across the setting-up retry cycle — analytics fires exactly once,
      // on the call that actually reveals the address. EDGE-32101: gate on `revealed?.address` too —
      // a name-matched network with no address is NOT a reveal (it falls to networkUnavailable), so
      // firing the "Reveal … Address" event there would be a false funnel signal.
      if (revealed?.address && !hasFiredAnalyticsRef.current) {
        hasFiredAnalyticsRef.current = true;
        reportEvent('Transactions > Reveal Crypto Direct Address', {
          network,
          asset,
          deposit_method: 'whop-crypto',
        });
      }
    },
    onSettled: (data) => {
      // Release the same-tick double-submit guard on EVERY settle — success, error, the 40s per-request
      // timeout, and the deadline's abort (the service rethrows the cancellation, so the mutation
      // settles here too). Placed ABOVE every early return so a fast-settling create leaves Retry
      // immediately pressable — the defect this fixes (Milan #3730 review).
      isSubmittingRef.current = false;
      // Post-unmount guard (see isClosedRef): an in-flight POST that lands after close still runs
      // this in v4. Short-circuit BEFORE arming a retry timer, or a closed sheet keeps POSTing.
      if (isClosedRef.current) return;
      // Deadline guard: once the 60s cycle deadline has fired (settingUpTimeout, lock released), a
      // late settle of an aborted/deadlined poll must NOT re-arm the cycle.
      if (isSettingUpTimedOutRef.current) return;
      const revealed = data?.networks.find((n) => n.name === network) ?? null;
      // EDGE-32101 (Milan review): gate on `revealed?.address`, not name alone. A provisioning
      // account whose picked network is present by NAME but address-less has NOT been revealed yet
      // (the address is still being issued) — so it must KEEP the cycle armed, matching the render
      // gate at `stillProvisioning` below and the success + analytics gates. Name-only here would
      // treat a present-but-address-less network as revealed and bail, stranding a provisioning
      // account on the terminal `networkUnavailable` instead of continuing to poll.
      const stillProvisioning = data?.accountStatus === 'provisioning' && !revealed?.address;
      if (!stillProvisioning) return;

      setIsProvisioningCycle(true);
      // Arm the 60s overall cycle deadline ONCE per cycle, HERE — at the ENTRY to settingUp (the
      // first `provisioning` settle, which is the first false→true transition of isProvisioningCycle).
      // Anchored synchronously to this settle (NOT to sheet open, NOT to the first create), so a slow
      // initial `loading` request — bounded separately by the 40s per-request timeout — never eats
      // the 60s budget. Arming here (not in an isProvisioningCycle-keyed effect) keeps it ordered
      // ahead of the reveal so the terminal-clear effect below always finds the timer to disarm. On
      // fire: abort the in-flight poll, clear any pending re-fire timer (it dispatches mutate()
      // DIRECTLY, bypassing this callback's guard, so it MUST be cleared here), and flip to
      // settingUpTimeout (unlocked, Retry) regardless of attempt count.
      if (!deadlineArmedRef.current) {
        deadlineArmedRef.current = true;
        deadlineTimeoutRef.current = setTimeout(() => {
          abortControllerRef.current?.abort();
          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = undefined;
          }
          // This flips to settingUpTimeout (Retry) SYNCHRONOUSLY — ahead of the aborted poll's onSettled
          // (which also releases the guard). Release it here too so every path that renders Retry leaves
          // the submit guard cleared, without depending on the abort→reject→onSettled ordering.
          isSubmittingRef.current = false;
          isSettingUpTimedOutRef.current = true;
          setIsSettingUpTimedOut(true);
        }, CRYPTO_SETUP_DEADLINE_MS);
      }
      // Check-then-arm BEFORE incrementing: 1 confirm + up to CRYPTO_SETUP_MAX_RETRIES re-fires =
      // 1 + CRYPTO_SETUP_MAX_RETRIES POSTs total. Incrementing first would exhaust the cycle one
      // POST early (see RED report).
      if (retryCountRef.current < CRYPTO_SETUP_MAX_RETRIES) {
        retryCountRef.current += 1;
        // Armed from THIS settled callback — never a bare interval — so at most one create call
        // is ever in flight.
        retryTimeoutRef.current = setTimeout(() => {
          mutation.mutate();
        }, CRYPTO_SETUP_RETRY_MS);
      } else {
        isSettingUpTimedOutRef.current = true;
        setIsSettingUpTimedOut(true);
      }
    },
  });

  // Shared by the initial "reveal" confirm and every Retry affordance: always calls the mutation
  // (the server records a disclosure on every confirm; a provisioned account costs zero Whop
  // calls) and restarts the setting-up cycle bookkeeping from zero.
  const startOrRetry = () => {
    // EDGE-32105 same-tick double-submit guard (see isSubmittingRef). The `'loading'` lock (see
    // `isLocked` below) is the PRIMARY prevention: the first press flips phase→loading, unmounting the
    // confirm/Retry button AND locking the sheet, so a second deliberate press is impossible once React
    // re-renders. This ref closes the residual SAME-TICK double-tap — a second click dispatched before
    // that re-render — by swallowing any press made while a create from THIS press is still in flight.
    // A swallowed call is a PURE no-op: it returns BEFORE the timer/latch resets below, so it never
    // tears down the in-flight cycle. Covers confirm + Retry (both route here); the automatic
    // provisioning re-fire calls mutation.mutate() directly and is intentionally NOT gated — so it never
    // sets this ref, and the guard only ever tracks a user-initiated press.
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = undefined;
    }
    // A fresh cycle: disarm the previous deadline, reset the once-per-cycle latch so the next entry
    // to settingUp re-arms it, and hand the cycle a fresh AbortController.
    if (deadlineTimeoutRef.current) {
      clearTimeout(deadlineTimeoutRef.current);
      deadlineTimeoutRef.current = undefined;
    }
    deadlineArmedRef.current = false;
    isSettingUpTimedOutRef.current = false;
    abortControllerRef.current = new AbortController();
    retryCountRef.current = 0;
    setIsSettingUpTimedOut(false);
    setIsProvisioningCycle(false);
    mutation.mutate();
  };

  const revealedNetwork = mutation.data?.networks.find((n) => n.name === network) ?? null;
  const stillProvisioning = mutation.data?.accountStatus === 'provisioning' && !revealedNetwork?.address;
  const cooldownError = mutation.error instanceof CryptoCooldownError ? mutation.error : null;

  let phase: Phase;
  if (isSettingUpTimedOut) {
    phase = 'settingUpTimeout';
  } else if (mutation.isError) {
    // EDGE-32105: with the server-side "Too many attempts" cooldown removed, the generic `'error'`
    // phase — which surfaces Retry via `showRetry` — is now the PRIMARY failure path. The
    // CryptoCooldownError branch is retained as TOLERATED LEGACY: an old gateway during deploy skew
    // may still 429, so its `cooldown` / `cooldownUnknown` copy stays intact for that window. No
    // functional `showRetry` change.
    if (cooldownError) {
      phase = cooldownError.retryAfterAt ? 'cooldown' : 'cooldownUnknown';
    } else {
      phase = 'error';
    }
  } else if (mutation.isSuccess) {
    // EDGE-32101: `success` requires the revealed (POST) network to HAVE an address — a reveal with
    // no address to show is not a valid reveal. A present-but-address-less picked network (name
    // matched, no address) is address-aware `stillProvisioning` (`!revealedNetwork?.address`, matching
    // the success + analytics gates): while the account is `provisioning` it stays `settingUp` and
    // keeps polling until the address is issued (Milan review); only a NON-provisioning account with
    // such a network falls through to the terminal `networkUnavailable` — never an empty success panel.
    if (revealedNetwork?.address) phase = 'success';
    else if (stillProvisioning) phase = 'settingUp';
    else phase = 'networkUnavailable';
  } else if (mutation.isLoading && isProvisioningCycle) {
    phase = 'settingUp';
  } else if (mutation.isIdle) {
    phase = 'initial';
  } else {
    phase = 'loading';
  }

  // Disarm the deadline the moment the cycle leaves the loading/settingUp phases for any terminal
  // (or idle) phase — a revealed `success` would otherwise keep the timer armed and, at +60s, flip
  // a shown address to `settingUpTimeout`. Does NOT reset deadlineArmedRef (that stays latched for
  // the cycle; a fresh cycle re-arms via startOrRetry).
  useEffect(() => {
    const isCycleActive = phase === 'loading' || phase === 'settingUp';
    if (!isCycleActive && deadlineTimeoutRef.current) {
      clearTimeout(deadlineTimeoutRef.current);
      deadlineTimeoutRef.current = undefined;
    }
  }, [phase]);

  let statusMessage: string | null = null;
  switch (phase) {
    case 'success':
      // The reveal is the whole point of the screen — announce it via the aria-live region so SR
      // users know the address is ready (every other phase already announces; 'success' fell to
      // null). Focus moves to the Copy control on mount of CryptoAddressPanel.
      statusMessage = t('deposits.cryptoDirect.sheet.revealed', { asset, network });
      break;
    case 'error':
      statusMessage = t('deposits.cryptoDirect.sheet.error');
      break;
    case 'networkUnavailable':
      statusMessage = t('deposits.cryptoDirect.sheet.networkUnavailable');
      break;
    case 'cooldown':
      statusMessage = t('deposits.cryptoDirect.sheet.cooldown', {
        time: formatCooldownTime(cooldownError?.retryAfterAt ?? null),
      });
      break;
    case 'cooldownUnknown':
      statusMessage = t('deposits.cryptoDirect.sheet.cooldownUnknown');
      break;
    case 'loading':
      // The single in-flight create request. Locks the sheet, so it MUST announce why (settingUp
      // already announces via sheet.settingUp; only `loading` was silent).
      statusMessage = t('deposits.cryptoDirect.sheet.loading');
      break;
    case 'settingUp':
      statusMessage = t('deposits.cryptoDirect.sheet.settingUp');
      break;
    case 'settingUpTimeout':
      statusMessage = t('deposits.cryptoDirect.sheet.settingUpTimeout');
      break;
    default:
      statusMessage = null;
  }

  const showRetry = phase === 'error' || phase === 'settingUpTimeout';
  const assetIconUrl = revealedNetwork?.assets.find((a) => a.symbol === asset)?.iconUrl ?? null;

  // EDGE-32059 UAT finding #3 (+ Mark's UAT MUST): lock the sheet during BOTH the single in-flight
  // create request (`loading`) AND the setting-up poll cycle (`settingUp`) — the whale screen's X is
  // disabled so the user can't abandon a half-provisioned account. Safe because the cycle is BOUNDED:
  // a 40s per-request timeout + a 60s overall deadline (both above) always release it into
  // `settingUpTimeout` (unlocked, Retry) or a terminal phase — never an inescapable modal (a11y
  // 2.1.2). Terminal phases stay UNLOCKED. It holds by construction (derived from `phase`), so it can
  // never drift from what's on screen.
  //   `&& !mutation.isPaused`: react-query v4's default networkMode 'online' marks an OFFLINE mutate()
  // as status 'loading' + isPaused true (queued, never sent). Without this guard the modal would lock
  // with no in-flight request and no reason until connectivity returns — so exclude the paused state.
  const isLocked = (phase === 'loading' || phase === 'settingUp') && !mutation.isPaused;
  // Loader shown for BOTH the single in-flight request and the setting-up poll cycle.
  const showLoader = phase === 'loading' || phase === 'settingUp';

  // Extracted so the lock-transition focus effect can find the close X by its accessible name.
  const closeButtonLabel = t('deposits.cryptoDirect.sheet.close');

  // WCAG 2.4.3 focus management across the sheet's phase/lock transitions. A phase change UNMOUNTS
  // the element that currently holds focus — confirm→loading unmounts the Reveal button; a Retry /
  // close X appears or vanishes between phases — which drops focus to <body>, stranding a keyboard /
  // SR user OUTSIDE the dialog. Keyed on the DERIVED `isLocked` + `phase` (never a one-shot mount
  // flag) so EVERY transition is handled — the first lock entry AND a settingUpTimeout→Retry
  // re-entry alike. On each transition, IF focus has escaped (element unmounted → <body>) or sits on
  // the bare dialog container, land it on the CURRENT phase's primary action, INSIDE the dialog:
  //   • locked (bounded loading/settingUp, nothing tabbable): the dialog container (Mantine's Paper,
  //     role="dialog" tabIndex={-1}) — focus stays inside the modal.
  //   • settingUpTimeout / error: the Retry button.
  //   • networkUnavailable / cooldown / cooldownUnknown: the close X.
  //   • success: DELIBERATELY left alone — CryptoAddressPanel autofocuses its Copy control on mount
  //     (a prior a11y fix; Copy IS the actionable element on reveal), so the parent no-ops rather
  //     than stealing focus to the X.
  //   • initial (a re-pick) / offline-paused loading (networkMode 'online' queues the mutate as
  //     isPaused, so isLocked stays false even as the Reveal button unmounts): the dialog container.
  // The first run is skipped so Mantine's focus trap owns the initial (Reveal-button) focus; a
  // transition that leaves focus already on a real interactive element inside the dialog is left be.
  const hasRunFocusEffectRef = useRef(false);
  useEffect(() => {
    const firstRun = !hasRunFocusEffectRef.current;
    hasRunFocusEffectRef.current = true;
    const dialog = dialogBodyRef.current?.closest<HTMLElement>('[role="dialog"]');
    if (!dialog || firstRun || phase === 'success') return;

    const active = document.activeElement;
    // Focus already rests on a real interactive element inside the dialog (not the bare container) —
    // something placed it deliberately; don't fight it.
    if (dialog.contains(active) && active !== dialog) return;

    if (isLocked || phase === 'initial' || phase === 'loading') {
      dialog.focus();
    } else if (phase === 'settingUpTimeout' || phase === 'error') {
      (retryButtonRef.current ?? dialog).focus();
    } else {
      const closeX = Array.from(dialog.querySelectorAll<HTMLElement>('button')).find(
        (button) => button.getAttribute('aria-label') === closeButtonLabel
      );
      (closeX ?? dialog).focus();
    }
  }, [isLocked, phase, closeButtonLabel]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      zIndex={theme.other.zIndex.cryptoAddressSheet}
      data-test-id={dt.payments.deposits.cryptoDirect.addressSheet}
      centered
      title={t('deposits.cryptoDirect.sheet.title', { asset, network })}
      withCloseButton={!isLocked}
      closeOnClickOutside={!isLocked}
      closeOnEscape={!isLocked}
      // Mantine renders the close button with aria-label={closeButtonLabel}; without it the X has NO
      // accessible name (a11y gap + untestable by role).
      closeButtonLabel={closeButtonLabel}
    >
      <Stack ref={dialogBodyRef} spacing={theme.other.spacing._16} align="center" w="100%">
        {/* EDGE-32059 UAT finding #5: a single plain back control replaces the redundant in-sheet
            `{network} › {asset}` trail — the sheet title ("{{asset}} on {{network}}") already says
            it. Visible label "‹ Back"; the accessible name carries the network context via the
            templated `backToAssets`. Wired to onClose (→ closeSheet → asset list) and HIDDEN while
            the sheet is locked (finding #3) so the user can't leave during the bounded
            loading/settingUp cycle. (The container's list steps use their gutter BackButton; there is
            no inline breadcrumb — EDGE-32114.) */}
        {!isLocked && (
          <Group spacing={theme.other.spacing._4} w="100%">
            <UnstyledButton
              onClick={onClose}
              aria-label={t('deposits.cryptoDirect.sheet.backToAssets', { network })}
              data-test-id={dt.payments.deposits.cryptoDirect.sheetBack}
            >
              <Text variant="subhead-medium">‹ {t('deposits.cryptoDirect.sheet.back')}</Text>
            </UnstyledButton>
          </Group>
        )}

        {phase === 'initial' && (
          <>
            <Button
              variant="primary-fill"
              size="medium"
              fullWidth
              onClick={startOrRetry}
              data-test-id={dt.payments.deposits.cryptoDirect.confirmReveal}
            >
              {t('deposits.cryptoDirect.sheet.reveal')}
            </Button>
            {/* EDGE-32375: set the wait expectation on the confirm step too — informational (muted,
                not the orange loss warning), the same notice slot the reveal warning uses. */}
            <Text variant="body-medium" c="gray2.6" ta="center">
              {t('deposits.cryptoDirect.sheet.expectedTime')}
            </Text>
          </>
        )}

        {phase === 'success' && revealedNetwork && (
          <>
            <CryptoAddressPanel network={revealedNetwork} assetIconUrl={assetIconUrl} />
            <Text variant="body-medium" c="gray2.6" ta="center">
              {t('deposits.cryptoDirect.sheet.warning', { asset, network })}
            </Text>
            <Text variant="body-medium" c="gray2.6" ta="center">
              {t('deposits.cryptoDirect.sheet.minimum')}
            </Text>
            {/* EDGE-32375: expected-time expectation on the reveal step, same muted notice slot. */}
            <Text variant="body-medium" c="gray2.6" ta="center">
              {t('deposits.cryptoDirect.sheet.expectedTime')}
            </Text>
            {/* EDGE-32497 (design §2.3): 30-day "address was updated" notice, same muted notice slot.
                Gated by the NaN-safe predicate so an unparseable timestamp never reaches date-fns'
                format() (which throws on an Invalid Date) and never costs the user the reveal. */}
            {shouldShowAddressChangedNotice(revealedNetwork.addressChangedAt, Date.now()) && (
              <div data-test-id={dt.payments.deposits.cryptoDirect.addressChangedNotice}>
                <Text variant="body-medium" c="gray2.6" ta="center">
                  {t('deposits.cryptoDirect.sheet.addressChanged', {
                    network,
                    date: formatAddressChangedDate(revealedNetwork.addressChangedAt),
                  })}
                </Text>
              </div>
            )}
          </>
        )}

        {/* EDGE-32059 UAT finding #3: the whale loader during BOTH the single in-flight request
            (`loading`) and the setting-up poll cycle (`settingUp`), ABOVE the aria-live status
            text (the `settingUp` copy still shows under it). SplashLoader takes only `children`, so
            the test-id lives on a wrapper. */}
        {showLoader && (
          <div data-test-id={dt.payments.deposits.cryptoDirect.sheetLoader}>
            <SplashLoader />
          </div>
        )}

        {/* Permanently mounted (never conditionally rendered) so async status changes are
            actually announced — its own region, separate from CryptoAddressPanel's copy-status
            live region. */}
        <div aria-live="polite">
          {statusMessage && (
            <Text variant="body-medium" c="gray2.6" ta="center">
              {statusMessage}
            </Text>
          )}
        </div>

        {showRetry && (
          <Button
            ref={retryButtonRef}
            variant="secondary-stroke"
            size="medium"
            onClick={startOrRetry}
            data-test-id={dt.payments.deposits.cryptoDirect.retryButton}
          >
            {t('deposits.cryptoDirect.error.retry')}
          </Button>
        )}

        {/* Terminal dead-end for THIS network: the only forward path is picking another one. NOT a
            retry (a re-POST would land the same absent shape) — a distinct back-to-network-list
            affordance with its own test-id, deliberately excluded from `showRetry`. */}
        {phase === 'networkUnavailable' && (
          <Button
            variant="secondary-stroke"
            size="medium"
            onClick={onBackToNetworkList}
            data-test-id={dt.payments.deposits.cryptoDirect.networkUnavailableBack}
          >
            {t('deposits.cryptoDirect.sheet.networkUnavailableAction')}
          </Button>
        )}
      </Stack>
    </Modal>
  );
}
